/**
 * Куда и какой высоты ставить подсказку маркера изменений.
 *
 * Вынесено из компонента, потому что это арифметика, а не разметка: у неё
 * есть правильный ответ, и его можно проверить числами, не поднимая DOM.
 *
 * Правило (по требованию 31.08): в день с несколькими правками список не
 * помещался, и прокрутить его было нельзя. Порядок такой —
 *   1. пока помещается, окно ровно по содержимому;
 *   2. не помещается — растём, но НЕ БОЛЕЕ ЧЕМ ВДВОЕ от базовой высоты;
 *   3. всё ещё не помещается — прокрутка внутри окна.
 *
 * ★ Рост ограничен ещё и экраном. Вырасти до 360 px у маркера, под которым
 * осталось 120, значит увести половину списка за нижний край — там нет ни
 * прокрутки, ни возможности дотянуться. Поэтому «вдвое» — это потолок, а не
 * обещание: фактическая высота всегда не больше свободного места.
 */

/** Базовая высота — сколько подсказка занимала до этой правки. */
export const TIP_BASE_MAX_H = 180;

/** Потолок роста: «вплоть до двух раз». */
export const TIP_GROWN_MAX_H = TIP_BASE_MAX_H * 2;

/** Ниже этого окно бесполезно: не видно даже одной строки. */
export const TIP_MIN_H = 120;

/** Зазор между маркером и подсказкой. */
const GAP = 6;

/** Не прижимать подсказку вплотную к краю экрана. */
const EDGE = 8;

export type TipSide = "below" | "above";

export type TipBox = {
  side: TipSide;
  /** Экранная координата верха подсказки (`position: fixed`). */
  top: number;
  /** `max-height` в пикселях; содержимое выше — прокручивается. */
  maxH: number;
};

/**
 * @param need        полная высота содержимого (`scrollHeight`)
 * @param anchorTop   верх маркера в экранных координатах
 * @param anchorBottom низ маркера в экранных координатах
 * @param viewportH   высота окна
 */
export function tooltipBox({
  need,
  anchorTop,
  anchorBottom,
  viewportH,
}: {
  need: number;
  anchorTop: number;
  anchorBottom: number;
  viewportH: number;
}): TipBox {
  const desired = Math.min(need, TIP_GROWN_MAX_H);
  const roomBelow = viewportH - anchorBottom - GAP - EDGE;
  const roomAbove = anchorTop - GAP - EDGE;

  // Снизу — сторона по умолчанию: подсказка висит под маркером, и
  // переворачивать её без нужды значит уводить взгляд от точки на линии.
  if (roomBelow >= desired) {
    return { side: "below", top: anchorBottom + GAP, maxH: desired };
  }

  // Снизу не поместилось: берём сторону с бо́льшим местом. Отдельной
  // ветки «сверху помещается целиком» здесь НЕТ намеренно — она давала
  // ровно тот же ответ, что и этот путь (проверено перебором), только
  // без зажима верхнего края. Мутация её удаления не роняла ни одного
  // теста — это и был признак, что ветка мёртвая, а не непокрытая.
  const below = roomBelow >= roomAbove;
  const room = Math.max(below ? roomBelow : roomAbove, TIP_MIN_H);
  const maxH = Math.min(desired, room);
  const top = below
    ? anchorBottom + GAP
    : Math.max(EDGE, anchorTop - GAP - maxH);
  return { side: below ? "below" : "above", top, maxH };
}
